// ─────────────────────────────────────────────────────────────────────────────
// The phone's keyboard must never cover the field being typed into.
//
// Three things, because the browsers disagree about what a keyboard does to the page:
//
// 1. Chrome on Android is told `interactive-widget=resizes-content` (the viewport meta in
//    pqforce-mobile.php): the keyboard then SHRINKS THE LAYOUT, so everything fixed to the screen —
//    the sheets, the search card — sits above it. Its default only shrank the visible area, and a
//    sheet fixed to the bottom of the screen stayed behind the keyboard.
// 2. Safari on iOS ignores that and shrinks only the VISUAL viewport. `--pqm-keyboard` on <html>
//    carries the height the keyboard covers (from `visualViewport`), and the stylesheet pads the
//    sheets' scrim by it. Under Chrome with (1) it is 0, so the two never add up.
// 3. Neither browser reliably scrolls a field that is already on screen out from under the
//    keyboard it is about to open, and none of them knows about the sticky header. So a focused
//    field is scrolled into the visible area here, once the keyboard has settled — inside its sheet
//    when it is in one, the page otherwise.
//
// While a field has the focus the root carries `pqm-typing`, which takes the module menu away: it
// would otherwise sit on top of the keyboard and take another 52px from what is left.
// ─────────────────────────────────────────────────────────────────────────────

/** The root class while a text field has the focus. */
export const TYPING_CLASS = "pqm-typing";

/** Below this, a shrunk visual viewport is the browser's address bar moving, not a keyboard. */
const KEYBOARD_MIN = 80;

/** Breathing room kept between a revealed field and the keyboard or the header. */
const REVEAL_MARGIN = 16;

/** How long the keyboard takes to slide in — after it, the visible area is final. */
const KEYBOARD_SETTLE_MS = 350;

/**
 * How long the visible area must stay put before a non-zero keyboard height is applied.
 *
 * ⚠ CHROME RESIZES IN TWO STEPS. With `resizes-content` the visual viewport shrinks first and the
 * layout follows a moment later — and in between, the visible area is short of the window by the
 * whole keyboard, which reads exactly like Safari's keyboard. Applied at once, it padded the sheets'
 * scrim by the keyboard's height for a frame or two: the translucent backdrop flashed up from the
 * bottom over the field. Waiting for the area to settle lets Chrome reach its final state (0)
 * first; Safari, where the gap is real, gets its lift this much later.
 */
const INSET_SETTLE_MS = 200;

/**
 * How much of the layout viewport the keyboard covers, in CSS pixels — 0 when there is none, or
 * when the browser already shrank the layout for it (Chrome with `resizes-content`).
 */
export function keyboardInset(innerHeight: number, visualHeight: number, visualOffsetTop: number): number {
    const covered = Math.round(innerHeight - visualHeight - visualOffsetTop);
    return covered >= KEYBOARD_MIN ? covered : 0;
}

/** Does typing into `element` open a keyboard? Checkboxes, radios and buttons do not. */
export function isTextEntry(element: Element | null): element is HTMLElement {
    if (!element) return false;
    if (element instanceof HTMLTextAreaElement) return true;
    if (element instanceof HTMLInputElement) {
        return !["checkbox", "radio", "button", "submit", "reset", "file", "range", "color", "hidden"].includes(
            element.type,
        );
    }
    return element instanceof HTMLElement && element.isContentEditable === true;
}

/**
 * How far to scroll so a field shows between `visibleTop` and `visibleBottom` — 0 when it already
 * does. Positive scrolls down. A field taller than the space is aligned to its top, where typing
 * starts.
 */
export function revealDelta(
    rect: { top: number; bottom: number },
    visibleTop: number,
    visibleBottom: number,
    margin: number = REVEAL_MARGIN,
): number {
    const top = visibleTop + margin;
    const bottom = visibleBottom - margin;
    if (rect.top >= top && rect.bottom <= bottom) return 0;
    if (rect.bottom - rect.top > bottom - top || rect.top < top) return Math.round(rect.top - top);
    return Math.round(rect.bottom - bottom);
}

/** The nearest ancestor that scrolls vertically — a sheet's panel, the search results — or null. */
function scrollParentOf(element: HTMLElement): HTMLElement | null {
    for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
        const overflow = getComputedStyle(node).overflowY;
        if ((overflow === "auto" || overflow === "scroll") && node.scrollHeight > node.clientHeight) return node;
    }
    return null;
}

/** Scroll `field` into the part of the screen the keyboard and the header leave visible. */
export function revealField(field: HTMLElement): void {
    if (!field.isConnected || document.activeElement !== field) return;

    const viewport = window.visualViewport;
    const visibleTop = viewport ? viewport.offsetTop : 0;
    const visibleBottom = visibleTop + (viewport ? viewport.height : window.innerHeight);

    // The sticky header covers the top of the PAGE; a field inside an overlay is above it.
    const inOverlay = field.closest("[data-overlay]") !== null;
    const header = inOverlay ? null : document.querySelector<HTMLElement>(".pqm-appbar");
    const top = Math.max(visibleTop, header ? header.getBoundingClientRect().bottom : visibleTop);

    const delta = revealDelta(field.getBoundingClientRect(), top, visibleBottom);
    if (delta === 0) return;

    const parent = scrollParentOf(field);
    if (parent) parent.scrollTop += delta;
    else window.scrollBy(0, delta);
}

/** The listeners; module-level so a second init (the tests mount many times) replaces them. */
let handlers: { focusin: (e: Event) => void; focusout: () => void; viewport: () => void } | null = null;

export function initKeyboard(root: HTMLElement): void {
    const viewport = window.visualViewport;
    if (handlers) {
        document.removeEventListener("focusin", handlers.focusin);
        document.removeEventListener("focusout", handlers.focusout);
        viewport?.removeEventListener("resize", handlers.viewport);
        viewport?.removeEventListener("scroll", handlers.viewport);
    }

    let revealTimer = 0;
    let insetTimer = 0;

    const apply = (inset: number) => document.documentElement.style.setProperty("--pqm-keyboard", `${inset}px`);

    const measure = () => {
        window.clearTimeout(insetTimer);
        const inset = viewport ? keyboardInset(window.innerHeight, viewport.height, viewport.offsetTop) : 0;
        // Taking the lift AWAY is never wrong and must not lag behind a closing keyboard; adding it
        // waits until the browser has finished resizing — see INSET_SETTLE_MS.
        if (inset === 0) {
            apply(0);
            return;
        }
        insetTimer = window.setTimeout(() => {
            const settled = viewport ? keyboardInset(window.innerHeight, viewport.height, viewport.offsetTop) : 0;
            apply(settled);
        }, INSET_SETTLE_MS);
    };

    const scheduleReveal = (field: HTMLElement) => {
        window.clearTimeout(revealTimer);
        revealTimer = window.setTimeout(() => revealField(field), KEYBOARD_SETTLE_MS);
    };

    handlers = {
        focusin: (event: Event) => {
            if (!isTextEntry(event.target as Element)) return;
            root.classList.add(TYPING_CLASS);
            scheduleReveal(event.target as HTMLElement);
        },
        focusout: () => {
            // On the NEXT frame: moving from one field to the next is a focusout then a focusin, and
            // dropping the class in between would flash the menu.
            window.requestAnimationFrame(() => {
                if (!isTextEntry(document.activeElement)) root.classList.remove(TYPING_CLASS);
            });
        },
        viewport: () => {
            measure();
            // The keyboard changed the visible area: settle the focused field again.
            if (isTextEntry(document.activeElement)) scheduleReveal(document.activeElement);
        },
    };

    document.addEventListener("focusin", handlers.focusin);
    document.addEventListener("focusout", handlers.focusout);
    viewport?.addEventListener("resize", handlers.viewport);
    viewport?.addEventListener("scroll", handlers.viewport);
    measure();
}
